import { ReactNode } from 'react';
import { infoToolTip, purpleRedirectIcon } from '../../../assets/hostedassets';
import LoadingSpinner from '../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SideDrawer from '../../../common/_custom/SideDrawer/SideDrawer';
import Tooltip from '../../../common/_custom/Tooltip/Tooltip';
import { isEmpty } from '../../../utils/utils';
import { RISK_MESSAGES_MAP, RISK_HEADER_KEYS } from '../RiskIndicatorConstants';
import {
    RegulatoryRiskDrawerCheckTypeConfig,
    RegulatoryRiskDrawerField,
    RegulatoryRiskFieldType,
    isHttpUrl,
    isMatched,
    formatScore,
    toDisplayString,
} from '../useRegulatoryRiskDrawerConfig';
import styles from './RegulatoryRiskDrawer.module.scss';

interface RegulatoryRiskDrawerProps {
    checkType: string;
    entityName?: string;
    matchCount: number;
    matches: Record<string, any>[];
    config?: RegulatoryRiskDrawerCheckTypeConfig;
    isLoading?: boolean;
    defaultChipLabel?: string;
    onClose: () => void;
}

const renderFieldValue = (field: RegulatoryRiskDrawerField, value: any) => {
    switch (field.type) {
        case RegulatoryRiskFieldType.SourceLink:
            if (!isHttpUrl(value)) return <span className={styles.FieldValue}>{toDisplayString(value)}</span>;
            return (
                <a className={styles.FieldLink} href={value} target="_blank" rel="noopener noreferrer">
                    <img className={styles.LinkIcon} src={purpleRedirectIcon} alt="" aria-hidden="true" />
                    View Source
                </a>
            );
        case RegulatoryRiskFieldType.ImageLink:
            if (!isHttpUrl(value)) return <span className={styles.FieldValue}>{toDisplayString(value)}</span>;
            return (
                <a className={styles.FieldLink} href={value} target="_blank" rel="noopener noreferrer">
                    <img className={styles.LinkIcon} src={purpleRedirectIcon} alt="" aria-hidden="true" />
                    View Image
                </a>
            );
        default:
            // text renders as-is; line breaks preserved via CSS (white-space: pre-line).
            // Non-primitive values (arrays / objects, e.g. Associations) are coerced to a readable string.
            return <span className={styles.FieldValue}>{toDisplayString(value)}</span>;
    }
};

const getFieldValue = (field: RegulatoryRiskDrawerField, hit: Record<string, any>): ReactNode => {
    // 'Other Parameters Matched': the config lists candidate params; the BE hit
    // holds each one's status. Show the labels of the ones that matched, as one row.
    if (field.type === RegulatoryRiskFieldType.MatchedParams) {
        const matchedLabels = (field.params ?? []).filter((p) => isMatched(hit?.[p.rawKey])).map((p) => p.label);
        if (matchedLabels.length === 0) return null;
        return <span className={styles.FieldValue}>{matchedLabels.join(', ')}</span>;
    }
    const value = hit?.[field.rawKey];
    if (isEmpty(value)) return null;
    return renderFieldValue(field, value);
};

const RegulatoryRiskDrawer = ({
    checkType,
    entityName,
    matches,
    config,
    isLoading,
    defaultChipLabel = 'Name Matched',
    onClose,
}: RegulatoryRiskDrawerProps) => {
    const title = config?.title || checkType;
    const chipLabel = config?.chipLabel || defaultChipLabel;
    const fields = config?.fields ?? [];
    const infoText = RISK_MESSAGES_MAP[checkType as RISK_HEADER_KEYS];
    // a hit with no displayable field (e.g. a clean/Green result echoing only the input) renders an empty card.
    // fields is empty while config is still loading (see the config-undefined test below) — don't filter then,
    // there's nothing yet to judge "content" against.
    const hasContent = (hit: Record<string, any>) =>
        fields.length === 0 || fields.some((field) => getFieldValue(field, hit) !== null);
    const renderableMatches = matches.filter(hasContent);

    return (
        <SideDrawer
            allowClose
            onClose={onClose}
            width="804px"
            showHeading
            heading={
                <div className={styles.HeaderTitle}>
                    <span className={styles.CheckTypeTitle}>{title}</span>
                    {!!infoText && (
                        // usePortal: HeaderTitle has overflow:hidden (for entity-name ellipsis), which
                        // clips a normally-rendered Tooltip popover. Portal escapes it (same pattern as
                        // MandateSharingDrawer / BasicDetailsSidebar tooltips inside other drawers).
                        <Tooltip content={infoText} direction="top" usePortal>
                            <img className={styles.InfoIcon} src={infoToolTip} alt="Information tooltip" />
                        </Tooltip>
                    )}
                    {!!entityName && (
                        <>
                            <span className={styles.Dot}>|</span>
                            <span className={styles.EntityName}>{entityName}</span>
                        </>
                    )}
                </div>
            }
            headerActions={
                <span className={styles.MatchesPill}>
                    {renderableMatches.length} {renderableMatches.length === 1 ? 'Match' : 'Matches'}
                </span>
            }
        >
            <div className={styles.Body}>
                {isLoading && (
                    <div className={styles.LoadingState}>
                        <LoadingSpinner color="#6021B3" height="1.5rem" />
                    </div>
                )}
                {!isLoading && renderableMatches.length === 0 && (
                    <div className={styles.EmptyState}>No adverse match found.</div>
                )}
                {!isLoading &&
                    renderableMatches.map((hit, index) => {
                        const chipValue = config?.nameMatchKey ? hit?.[config.nameMatchKey] : undefined;
                        const showChip = !isEmpty(chipValue);

                        return (
                            <div className={styles.HitCard} key={index}>
                                <div className={styles.HitCardHeader}>
                                    <span className={styles.MatchLabel}>Match {index + 1}</span>
                                    {showChip && (
                                        <span className={styles.NameMatchChip}>
                                            {chipLabel} {formatScore(chipValue)}
                                        </span>
                                    )}
                                </div>
                                {fields.map((field) => {
                                    const fieldValue = getFieldValue(field, hit);
                                    if (!fieldValue) return null;
                                    return (
                                        <div className={styles.FieldRow} key={field.rawKey}>
                                            <span className={styles.FieldLabel}>{field.label}</span>
                                            {fieldValue}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
            </div>
        </SideDrawer>
    );
};

export default RegulatoryRiskDrawer;
